import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MP_GUIDES } from "@/lib/mp-guides";
import { MP_COMPARISONS } from "@/lib/mp-compare";
import { MARKETPLACE_OG_IMAGE, MARKETPLACE_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/guides/")({
  head: () =>
    buildPageMetadata({
      path: "/marketplace/guides",
      title: "Marketplace Guides — Sell and Buy Creator Services | BeHot.Now",
      description:
        "Every BeHot.Now Marketplace guide in one place: how it works, selling, buying, fees, featuring, boards, verification, payouts, orders, disputes, categories and tags.",
      ogTitle: "BeHot.Now Marketplace guides",
      ogDescription:
        "Practical guides for sellers and buyers on the BeHot.Now Marketplace: publishing, pricing, promotion, verification and payouts.",
      image: MARKETPLACE_OG_IMAGE,
      imageAlt: MARKETPLACE_OG_IMAGE_ALT,
      keywords: [
        "marketplace guides",
        "how to sell services",
        "how to buy creator services",
        "marketplace fees guide",
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: "Guides", path: "/marketplace/guides" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "BeHot.Now Marketplace guides",
        url: `${SITE_URL}/marketplace/guides`,
        hasPart: MP_GUIDES.map((g) => ({
          "@type": "WebPage",
          name: g.h1,
          url: `${SITE_URL}/marketplace/guides/${g.slug}`,
          description: g.description,
        })),
      },
    }),
  component: GuidesHub,
});

function GuidesHub() {
  const { t } = useT();
  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
        <BookOpen className="size-3.5" /> {t("mpseo.guides")}
      </span>
      <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl">{t("mpseo.hub.h1")}</h1>
      <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">{t("mpseo.hub.intro")}</p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {MP_GUIDES.map((g) => (
          <Link
            key={g.slug}
            to="/marketplace/guides/$slug"
            params={{ slug: g.slug }}
            className="card-surface group p-4 transition hover:border-primary/50"
          >
            <h2 className="font-display font-bold">{g.h1}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{g.description}</p>
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary">
              {t("mpseo.read")} <ArrowRight className="size-4" />
            </span>
          </Link>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("mpseo.compareOthers")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {MP_COMPARISONS.map((c) => (
            <Link
              key={c.slug}
              to="/compare/marketplace/$competitor"
              params={{ competitor: c.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {t("mpseo.vs", { brand: c.brand })}
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-10 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/marketplace/explore" search={{ q: undefined }}>{t("mpseo.cta.explore")}</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/marketplace/sell">{t("mpseo.cta.sell")}</Link>
        </Button>
      </div>
    </div>
  );
}
